/**
 * The journey auth token — written in one place, so a failed API call cannot destroy a session.
 *
 * `localStorage.setItem(key, undefined)` stores the STRING "undefined". Twelve screens wrote the
 * token straight from a response (`result?.token`), so any call that failed replaced a working
 * token with that string, and every request afterwards went out as `Authorization: Bearer
 * undefined` and came back 401.
 *
 * Caught on production: opening the public partner page routed the visitor using a leftover
 * session, then called `b2b/onboard/start`, got a 400, and wrote `undefined` over a valid token.
 * That is why the bug looked intermittent — the page destroyed its own session on the way out, so
 * the next visit behaved differently from the one before.
 *
 * On a missing token we KEEP whatever is already stored rather than clearing it. A failed refresh
 * is not evidence that the existing session is invalid, and logging a tenant out mid-journey is a
 * worse outcome than carrying on with the token they had.
 */

const TOKEN_KEY = "SIGNUP_TOKEN_CP";

/** Is this a value we are willing to send as a bearer token? */
export function isUsableJourneyToken(token) {
  if (typeof token !== "string") return false;
  const trimmed = token.trim();
  // "undefined" and "null" are the stringified accidents this module exists to stop; they are
  // never real tokens, and a blank string is not one either.
  return (
    trimmed !== "" && trimmed !== "undefined" && trimmed !== "null"
  );
}

/**
 * Store the journey token, ignoring anything that is not one.
 * @returns {boolean} whether the value was actually stored
 */
export function setJourneyToken(token) {
  if (!isUsableJourneyToken(token)) {
    // Deliberately quiet in production terms but visible while debugging: a skipped write means an
    // API did not return the token the caller expected, which is worth seeing next to the failure.
    console.warn(
      "Refusing to store a non-token as the journey token; keeping the existing session",
      { received: typeof token },
    );
    return false;
  }
  localStorage.setItem(TOKEN_KEY, token.trim());
  return true;
}

/** Read the journey token, treating a previously corrupted value as absent. */
export function getJourneyToken() {
  const stored = localStorage.getItem(TOKEN_KEY);
  return isUsableJourneyToken(stored) ? stored : null;
}
